import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

export interface CellDiffValue {
  beforeValue?: string;
  afterValue?: string;
  beforePreview?: string;
  afterPreview?: string;
  rawDiffText?: string;
}

type CellDiffRenderMode = "visual" | "text";

interface CellDiffRendererProps {
  diff: CellDiffValue;
  compact?: boolean;
  mode?: CellDiffRenderMode;
}

const CELL_DIFF_VIEWER_STYLES = {
  variables: {
    light: {
      diffViewerBackground: "transparent",
      diffViewerColor: "rgb(51, 65, 85)",
      addedBackground: "rgba(16, 185, 129, 0.1)",
      addedColor: "rgb(4, 120, 87)",
      removedBackground: "rgba(244, 63, 94, 0.1)",
      removedColor: "rgb(190, 18, 60)",
      wordAddedBackground: "rgba(16, 185, 129, 0.22)",
      wordRemovedBackground: "rgba(244, 63, 94, 0.2)",
      gutterBackground: "transparent",
      gutterColor: "rgb(148, 163, 184)",
    },
  },
  diffContainer: {
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 13,
    lineHeight: "20px",
  },
  contentText: {
    fontSize: 13,
    lineHeight: "20px",
  },
  line: {
    wordBreak: "break-word",
  },
} as const;

const getDiffTextValues = (diff: CellDiffValue): { beforeText: string; afterText: string } => ({
  beforeText: diff.beforeValue ?? diff.beforePreview ?? "",
  afterText: diff.afterValue ?? diff.afterPreview ?? "",
});

const wrapperClassByMode = (_mode: CellDiffRenderMode, compact: boolean): string => {
  if (compact) {
    return "h-full w-full overflow-hidden";
  }

  return "rounded-lg border border-border/60 bg-background p-3";
};

export function CellDiffRenderer({
  diff,
  compact = false,
  mode = "visual",
}: CellDiffRendererProps) {
  const { beforeText, afterText } = getDiffTextValues(diff);
  const isEqualComparison = beforeText === afterText;

  if (mode === "text") {
    return (
      <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-sm text-foreground/90">
        {diff.rawDiffText ?? ""}
      </pre>
    );
  }

  if (compact && isEqualComparison) {
    return (
      <div className="flex h-full w-full items-center px-2 text-xs text-muted-foreground">
        Values are equal
      </div>
    );
  }

  return (
    <div className={wrapperClassByMode(mode, compact)}>
      <ReactDiffViewer
        oldValue={beforeText || " "}
        newValue={afterText || " "}
        splitView={false}
        hideLineNumbers={compact}
        showDiffOnly
        extraLinesSurroundingDiff={compact ? 0 : 3}
        compareMethod={DiffMethod.WORDS}
        styles={CELL_DIFF_VIEWER_STYLES}
      />
    </div>
  );
}
